import React from 'react';

export default function DetailPanel({
  t,
  language,
  data,
  handlers
}) {
  const {
    selectedDate,
    holidaysForSelectedDate,
    isSelectedFavorite,
    zodiacDisplay,
    selectedZodiacAnimal,
    zodiacEmoji,
    selectedZodiacInfo,
    selectedZodiacSign,
    westernZodiacSymbol,
    dayCanChi,
    auspiciousHours,
    selectedNotes
  } = data;
  const { getHolidayName, onToggleFavorite, formatHourLabel } = handlers;

  return (
    <div className="card">
      <div className="section-header">
        <h3 className="section-title">{t.detailsTitle}</h3>
        {selectedDate && (
          <button
            className="primary-button"
            onClick={() => onToggleFavorite(selectedDate)}
          >
            {isSelectedFavorite ? t.removeFavorite : t.addFavorite}
          </button>
        )}
      </div>

      {selectedDate ? (
        <div className="details-stack">
          {holidaysForSelectedDate.length > 0 && (
            <section className="detail-section holiday-banner">
              <div className="holiday-icon">🎉</div>
              <div className="holiday-list-stack">
                {holidaysForSelectedDate.map((holiday) => (
                  <div key={`${holiday.code || holiday._id}-${holiday.solarDate || holiday.lunarDate}`} className="holiday-item-stack">
                    <p className="holiday-title">{getHolidayName(holiday)}</p>
                    {holiday.is_public_holiday && (
                      <span className="badge-official">{t.officialHoliday}</span>
                    )}
                    <p className="holiday-meta">
                      {holiday.type === 'lunar'
                        ? `🌙 ${t.lunar}: ${holiday.lunarDate}`
                        : `📅 ${t.solar}: ${holiday.solarDate}`}
                    </p>
                  </div>
                ))}
              </div>
            </section>
          )}

          <section className="detail-section">
            <div className="section-heading">
              <span className="section-icon">🗓️</span>
              <div>
                <p className="detail-label">{t.zodiacYear}</p>
                <p className="detail-value emphasis">{zodiacDisplay}</p>
                {selectedZodiacAnimal && (
                  <span className="zodiac-emoji">{zodiacEmoji} {selectedZodiacAnimal}</span>
                )}
              </div>
            </div>
            {selectedZodiacInfo && (
              <div className="zodiac-info-grid">
                <div>
                  <p className="chip-label">{t.zodiacElement}</p>
                  <p className="chip-value">
                    {typeof selectedZodiacInfo.element === 'string'
                      ? selectedZodiacInfo.element
                      : selectedZodiacInfo.element?.[language] || selectedZodiacInfo.element?.vi}
                  </p>
                </div>
                <div>
                  <p className="chip-label">{t.zodiacTraits}</p>
                  <p className="chip-value">
                    {typeof selectedZodiacInfo.personality === 'string'
                      ? selectedZodiacInfo.personality
                      : selectedZodiacInfo.personality?.[language] || selectedZodiacInfo.personality?.vi}
                  </p>
                </div>
              </div>
            )}
          </section>

          {selectedZodiacSign && (
            <section className="detail-section">
              <div className="section-heading">
                <span className="section-icon">🔮</span>
                <div>
                  <p className="detail-label">{t.zodiacSignWestern}</p>
                  <p className="detail-value emphasis">{westernZodiacSymbol} {selectedZodiacSign[language] || selectedZodiacSign.name}</p>
                </div>
              </div>
              <div className="zodiac-info-grid">
                <div>
                  <p className="chip-label">{t.zodiacElement}</p>
                  <p className="chip-value">{selectedZodiacSign.element}</p>
                </div>
                <div>
                  <p className="chip-label">{t.zodiacPeriod}</p>
                  <p className="chip-value">{selectedZodiacSign.dates}</p>
                </div>
              </div>
            </section>
          )}

          <section className="detail-section">
            <div className="section-heading">
              <span className="section-icon">⭐</span>
              <div>
                <p className="detail-label">{t.auspiciousTitle}</p>
                {dayCanChi && (
                  <p className="chip-label">
                    {`${t.dayPrefix} ${dayCanChi.label}`}
                  </p>
                )}
              </div>
            </div>
            <div className="hours-grid">
              <div>
                <p className="chip-label">✅ {t.goodHours}</p>
                <div className="hour-chips">
                  {auspiciousHours.good.map((hour) => {
                    const label = formatHourLabel(hour);
                    const key = typeof hour === 'string' ? hour : (hour.branch || label);
                    return (
                      <span key={key} className="hour-badge good">🟢 {label}</span>
                    );
                  })}
                </div>
              </div>
              <div>
                <p className="chip-label">❌ {t.badHours}</p>
                <div className="hour-chips">
                  {auspiciousHours.bad.map((hour) => {
                    const label = formatHourLabel(hour);
                    const key = typeof hour === 'string' ? hour : (hour.branch || label);
                    return (
                      <span key={key} className="hour-badge bad">🔴 {label}</span>
                    );
                  })}
                </div>
              </div>
            </div>
          </section>

          {selectedNotes.length > 0 && (
            <section className="detail-section">
              <div className="section-heading">
                <span className="section-icon">📝</span>
                <p className="detail-label">{t.notes}</p>
              </div>
              {selectedNotes.map((note, index) => (
                <p key={`${note}-${index}`} className="detail-description">{note}</p>
              ))}
            </section>
          )}
        </div>
      ) : (
        <p className="muted-text">{t.selectDate}</p>
      )}
    </div>
  );
}
